<template>
  <div
    ref="button"
    :class="[
      'vgp-interactive-element',
      $style.button,
      ...typeClasses,
      disabled ? $style.disabled : '',
      isLoading ? $style.loading : '',
      customClass,
    ]"
    :style="rootStyle"
    @click="onClick"
  >
    <div
      :style="margins"
      :class="$style.wrapper"
    >
      <div
        :class="[sideClasses, $style['text-area']]"
      >
        <template v-if="!isLoading">
          <div :class="$style.text">
            {{ label }}
          </div>
          <div
            v-if="icon"
            :class="[iconClass]"
          >
            <sprite
              :name="icon.name"
              :type="icon.type"
              :size="icon.size"
            />
          </div>
        </template>
        <ve-progress
          v-else
          v-bind="loaderConfig"
        />
      </div>
      <vgp-label
        v-if="pricingLabel"
        :class="$style['pricing-label']"
        type="text"
        size="extraExtraSmall"
        :value="pricingLabel"
        bg-color="#FFEBCC"
        text-color="#CC7B00"
      />
    </div>
  </div>
</template>

<script>
import { VeProgress } from 'vue-ellipse-progress';
import { BUTTON_TYPE_TO_LOADER_CONFIG_MAP } from './vgpButton.config';
import sprite from '$$uiKitComp/VueIcon/svgSprite.vue';
import vgpLabel from '$$vueCmp/label/vgpLabel.vue';

export default {
  name: 'VgpButton',
  components: {
    sprite,
    VeProgress,
    vgpLabel,
  },
  props: {
    label: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      required: true,
      validator(value) {
        return [
          'primary',
          'secondary',
          'tertiary',
          'destructive',
          'destructive-outline',
          'brand-fill',
          'brand-flat',
          'text-blue',
          'text-grey',
        ].includes(value);
      },
    },
    icon: {
      type: Object, // { type, name, size }
      default: null,
    },
    iconSide: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    xSmall: {
      type: Boolean,
      default: false,
    },
    pricingLabel: {
      type: String,
      default: null,
    },
    width: {
      type: String,
      default: '',
    },
    hasMargin: { type: Boolean, required: false, default: true },
    customClass: {
      type: [String, Array, Object],
      default: null,
    },
    customStyle: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'onClick',
  ],
  data() {
    return {
      iconSideData: this.iconSide,
      rememberedWidthBeforeLoadingState: null,
    };
  },
  computed: {
    typeClasses() {
      const size = this.small ? 'small' : this.xSmall ? 'x-small' : '';

      return [
        this.$style[`${this.type}-btn`],
        size ? this.$style[size] : '',
      ];
    },
    sideClasses() {
      return this.iconSideData ? this.$style[`button-icon-${this.iconSideData}`] : '';
    },
    iconClass() {
      return this.iconSideData ? this.$style[`button-icon-${this.iconSideData}`] : '';
    },
    loaderConfig() {
      if (BUTTON_TYPE_TO_LOADER_CONFIG_MAP[this.type]) {
        return BUTTON_TYPE_TO_LOADER_CONFIG_MAP[this.type];
      }

      return BUTTON_TYPE_TO_LOADER_CONFIG_MAP.primary;
    },
    size() {
      const width = this.width || `${this.rememberedWidthBeforeLoadingState}px`;
      const height = this.xSmall ? 24 : this.small ? 32 : 40;

      const style = {
        height: `${height}px`,
        width: `${width}`,
      };

      if (this.width === 'auto') {
        style.padding = '0 24px';
      }

      return style;
    },
    rootStyle() {
      if (!this.customStyle) {
        return this.size;
      }

      return { ...this.size, ...this.customStyle };
    },
    margins() {
      if (!this.hasMargin) return '';

      if (this.type === 'text-blue' || this.type === 'text-grey') {
        return '';
      }

      const margin = this.xSmall ? 8 : this.small ? 24 : 32;

      return {
        margin: `${this.width ? 0 : `0 ${margin}`}px`,
      };
    },
  },
  watch: {
    icon: {
      handler(val) {
        if (val) {
          this.iconSideData = this.iconSide ? this.iconSide : 'left';
          if (!this.label) {
            this.iconSideData = '';
          }
        }
      },
      immediate: true,
    },
    iconSide(val) {
      if (val) {
        this.iconSideData = this.iconSide;
      }
    },
    label(val) {
      if (!val) {
        this.iconSideData = '';
      }
    },
    isLoading(val) {
      if (val && !this.width) {
        this.rememberedWidthBeforeLoadingState = this.$refs.button.offsetWidth;
      } else {
        this.rememberedWidthBeforeLoadingState = null;
      }
    },
  },
  methods: {
    onClick() {
      !this.disabled && !this.isLoading && this.$emit('onClick');
    },
  },
};
</script>

<style module lang="less" src='./globalButton.less'></style>
